import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';

const BUS_DURATION = 4.5;
const DROP_DURATION = 0.4;

type BusTrailPuff = {
  mesh: THREE.Mesh;
  life: number;
  maxLife: number;
};

export type FpsBattleBusPhase = 'bus' | 'drop' | 'done';

export type FpsBattleBusUpdate = {
  phase: FpsBattleBusPhase;
  busPosition?: THREE.Vector3;
};

export type FpsBattleBusCameraFollow = {
  position: THREE.Vector3;
  lookAt: THREE.Vector3;
};

export class FpsBattleBus {
  private readonly root = new THREE.Group();
  private readonly busVisual = new THREE.Group();
  private readonly pathStart: THREE.Vector3;
  private readonly pathEnd: THREE.Vector3;
  private readonly toonGrad: THREE.DataTexture;

  private elapsed = 0;
  private phase: FpsBattleBusPhase = 'done';
  private onSkip: (() => void) | undefined;
  private trailAcc = 0;
  private readonly trailPuffs: BusTrailPuff[] = [];
  private readonly trailGeo = new THREE.SphereGeometry(0.22, 6, 6);
  private readonly trailMat = new THREE.MeshBasicMaterial({
    color: 0x88ccff,
    transparent: true,
    opacity: 0.45,
    depthWrite: false,
  });

  constructor(
    scene: THREE.Scene,
    opts?: {
      pathStart?: THREE.Vector3;
      pathEnd?: THREE.Vector3;
    },
  ) {
    this.toonGrad = createToonGradientMap();
    this.pathStart = opts?.pathStart?.clone() ?? new THREE.Vector3(-90, 72, -70);
    this.pathEnd = opts?.pathEnd?.clone() ?? new THREE.Vector3(90, 68, 70);

    const body = new THREE.Mesh(
      new THREE.BoxGeometry(4.2, 2.2, 7.5),
      toonMat(0xe8f06a, this.toonGrad, { emissive: 0x333010, emissiveIntensity: 0.15 }),
    );
    body.castShadow = true;
    body.receiveShadow = true;

    const trim = new THREE.Mesh(
      new THREE.BoxGeometry(4.25, 0.35, 7.55),
      toonMat(0xff9944, this.toonGrad, { emissive: 0x331808, emissiveIntensity: 0.12 }),
    );
    trim.position.y = 1.2;

    this.busVisual.add(body, trim);
    this.root.add(this.busVisual);
    scene.add(this.root);

    this.root.position.copy(this.pathStart);
  }

  start(onSkip?: () => void): void {
    this.onSkip = onSkip;
    this.elapsed = 0;
    this.phase = 'bus';
    this.root.position.copy(this.pathStart);
    this.root.visible = true;
  }

  skip(): void {
    if (this.phase === 'done') return;
    this.phase = 'done';
    this.elapsed = BUS_DURATION + DROP_DURATION + 1;
    this.root.visible = false;
    this.onSkip?.();
  }

  /**
   * Normalized progress along the bus leg (0..1). Use with getCameraFollow for a chase view.
   */
  getBusProgress01(): number {
    return Math.min(1, Math.max(0, this.elapsed / BUS_DURATION));
  }

  getPosition(): THREE.Vector3 {
    return this.root.position.clone();
  }

  getPathStart(): THREE.Vector3 {
    return this.pathStart.clone();
  }

  getPathEnd(): THREE.Vector3 {
    return this.pathEnd.clone();
  }

  update(dt: number): FpsBattleBusUpdate {
    if (this.phase === 'done') {
      return { phase: 'done' };
    }

    this.elapsed += dt;
    const out: FpsBattleBusUpdate = { phase: this.phase };

    if (this.phase === 'bus') {
      const t = Math.min(1, this.elapsed / BUS_DURATION);
      this.root.position.lerpVectors(this.pathStart, this.pathEnd, t);
      out.busPosition = this.root.position.clone();
      this.trailAcc += dt;
      if (this.trailAcc > 0.07) {
        this.trailAcc = 0;
        this.spawnTrailPuff();
      }
      this.updateTrailPuffs(dt);

      if (this.elapsed >= BUS_DURATION) {
        this.phase = 'drop';
      }
      return out;
    }

    if (this.phase === 'drop') {
      out.busPosition = this.pathEnd.clone();
      this.root.position.copy(this.pathEnd);
      this.updateTrailPuffs(dt);
      if (this.elapsed >= BUS_DURATION + DROP_DURATION) {
        this.phase = 'done';
        this.root.visible = false;
        return { phase: 'done' };
      }
      return out;
    }

    return { phase: 'done' };
  }

  getCameraFollow(busT: number): FpsBattleBusCameraFollow {
    const t = Math.min(1, Math.max(0, busT));
    const busPos = new THREE.Vector3().lerpVectors(this.pathStart, this.pathEnd, t);
    const tangent = new THREE.Vector3().subVectors(this.pathEnd, this.pathStart).normalize();
    const side = new THREE.Vector3().crossVectors(tangent, new THREE.Vector3(0, 1, 0)).normalize();
    const position = busPos
      .clone()
      .addScaledVector(side, 14)
      .addScaledVector(new THREE.Vector3(0, 1, 0), 8);
    const lookAt = busPos.clone().addScaledVector(tangent, 2);
    return { position, lookAt };
  }

  private spawnTrailPuff(): void {
    const mesh = new THREE.Mesh(this.trailGeo, this.trailMat);
    mesh.position.copy(this.root.position);
    mesh.position.y -= 0.8 + Math.random() * 0.4;
    mesh.position.x += (Math.random() - 0.5) * 1.2;
    mesh.position.z += (Math.random() - 0.5) * 1.2;
    this.root.parent?.add(mesh);
    this.trailPuffs.push({ mesh, life: 0, maxLife: 0.85 + Math.random() * 0.35 });
  }

  private updateTrailPuffs(dt: number): void {
    for (let i = this.trailPuffs.length - 1; i >= 0; i--) {
      const p = this.trailPuffs[i]!;
      p.life += dt;
      const t = p.life / p.maxLife;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = (1 - t) * 0.42;
      const s = 1 + t * 2.4;
      p.mesh.scale.setScalar(s);
      p.mesh.position.y -= dt * 1.6;
      if (p.life >= p.maxLife) {
        p.mesh.removeFromParent();
        this.trailPuffs.splice(i, 1);
      }
    }
  }

  dispose(): void {
    for (const p of this.trailPuffs) p.mesh.removeFromParent();
    this.trailPuffs.length = 0;
    this.trailGeo.dispose();
    this.trailMat.dispose();
    this.toonGrad.dispose();
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry?.dispose();
        if (o.material instanceof THREE.Material) o.material.dispose();
      }
    });
    this.root.removeFromParent();
  }
}
